import { html } from '@shared/utils/rendering/html';
import { useState, useEffect, useMemo, useRef } from 'preact/hooks';
import { toast } from 'react-toastify';
import { BarChart } from '@shared/components/charts';
import { CHART_COLORS } from '@shared/constants/chart-palette';
import { Icon, IconNames } from '@shared/components/icons';
import Button from '@shared/components/buttons';
import Dropdown from '@shared/components/dropdowns';
import { LoaderWithTitle } from '@shared/components/loading/loader-with-title';
import { useCachedFetch } from '@core/utils/hooks/use-cached-fetch';
import { SarbSelfAssessmentService } from '@core/utils/api/sarb-self-assessment.service';
import { useNavigate } from '@core/utils/context/navigation-context';
import { RdarrRoutingUrls } from '@core/constants/routing/urls.constants';
import { ConsolidatedExportModal } from './consolidated-export-modal';
import { buildConsolidatedRows } from './grouping';
import { SarbReportingMonths } from '@core/constants/sarb-self-assessment.constants';
import './index.scss';

export const ConsolidatedReportScreen = ({ onBack, orgFilters = {} }) => {
    const { onNavigate } = useNavigate();
    const [selectedYear, setSelectedYear] = useState(null);
    const [showExportModal, setShowExportModal] = useState(false);
    const [septemberReport, setSeptemberReport] = useState(null);
    const [decemberReport, setDecemberReport] = useState(null);
    const [isLoadingSept, setIsLoadingSept] = useState(false);
    const [isLoadingDec, setIsLoadingDec] = useState(false);
    const initializedRef = useRef(false);
    const abortRef = useRef({});

    // All legal entities — bypasses user-role filter for the consolidated view
    const { data: allLegalEntities = [] } = useCachedFetch('/legal-entities/all');
    const allEntities = useMemo(
        () => allLegalEntities.map((e) => ({ id: e.value, name: e.text })).filter((e) => e.name),
        [allLegalEntities]
    );

    const { data: allPeriods = [] } = useCachedFetch('/periods');

    const availableYears = useMemo(() => {
        if (!allPeriods?.length) return [];
        return [...new Set(allPeriods.map((p) => new Date(p.endDate).getFullYear()))].sort(
            (a, b) => b - a
        );
    }, [allPeriods]);

    // Venus Dropdown expects option objects { value, text }
    const yearOptions = useMemo(
        () => availableYears.map((y) => ({ value: y, text: String(y) })),
        [availableYears]
    );
    const selectedYearOption = useMemo(
        () => yearOptions.find((o) => o.value === selectedYear) ?? null,
        [yearOptions, selectedYear]
    );

    // One-time init: pre-select from current org filter period; user can change freely afterwards
    useEffect(() => {
        if (initializedRef.current || !availableYears.length) return;
        initializedRef.current = true;
        const end = orgFilters?.period?.endDate ? new Date(orgFilters.period.endDate) : null;
        setSelectedYear(end?.getFullYear() ?? availableYears[0]);
    }, [availableYears]);

    // Named handlers — inline arrow functions in event props cause unnecessary re-renders
    const handleYearChange = (option) => {
        if (option?.value != null) setSelectedYear(Number(option.value));
    };
    const handleOpenExport = () => setShowExportModal(true);
    const handleCloseExport = () => setShowExportModal(false);
    const handleBreadcrumbBack = (e) => {
        e.preventDefault();
        onBack();
        onNavigate(RdarrRoutingUrls.sarbSelfAssessment);
    };

    const findPeriodId = (year, month) => {
        if (!allPeriods?.length || year === null) return null;
        return (
            allPeriods.find((p) => {
                const d = new Date(p.endDate);
                return d.getFullYear() === year && d.getMonth() + 1 === month;
            })?.value ?? null
        );
    };

    const septemberPeriodId = useMemo(
        () => findPeriodId(selectedYear, SarbReportingMonths.September),
        [allPeriods, selectedYear]
    );
    const decemberPeriodId = useMemo(
        () => findPeriodId(selectedYear, SarbReportingMonths.December),
        [allPeriods, selectedYear]
    );

    const loadReport = async (periodKey, periodId, setData, setLoading) => {
        if (abortRef.current[periodKey]) abortRef.current[periodKey].abort();
        abortRef.current[periodKey] = new AbortController();
        setLoading(true);
        try {
            // No entity IDs = API returns all entities' SARB data for the period
            const data = await SarbSelfAssessmentService.getConsolidatedReport(periodId);
            setData(data);
        } catch (error) {
            if (error.name !== 'AbortError') {
                // 404 = no SARB assessments exist yet for this period — not an error
                if (error.response?.status !== 404) {
                    toast.error(`Failed to load ${periodKey} consolidated report`);
                }
                setData(null);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSeptemberReport(null);
        if (septemberPeriodId)
            loadReport('september', septemberPeriodId, setSeptemberReport, setIsLoadingSept);
    }, [septemberPeriodId]);

    useEffect(() => {
        setDecemberReport(null);
        if (decemberPeriodId)
            loadReport('december', decemberPeriodId, setDecemberReport, setIsLoadingDec);
    }, [decemberPeriodId]);

    const consolidatedRows = useMemo(
        () => buildConsolidatedRows({ allEntities, septemberReport, decemberReport }),
        [allEntities, septemberReport, decemberReport]
    );

    const anyPeriodExists = septemberPeriodId !== null || decemberPeriodId !== null;
    const isLoading = isLoadingSept || isLoadingDec;

    // Vertical bar chart: height is fixed — entity count drives width, not height
    const chartHeight = 550;

    const chartData = useMemo(() => {
        if (!consolidatedRows.length) return null;
        return {
            // Horizontal chart: entity names are the Y-axis categories
            categories: consolidatedRows.map((row) => row.name),
            datasets: [
                { name: 'September', values: consolidatedRows.map((row) => row.septemberRating) },
                { name: 'December', values: consolidatedRows.map((row) => row.decemberRating) },
            ],
        };
    }, [consolidatedRows]);

    const chartConfig = useMemo(
        () => ({
            horizontal: false,
            stacked: false,
            borderRadius: 4,
            columnWidth: '55%',
            showDataLabels: false,
            overrides: {
                plotOptions: {
                    bar: {
                        horizontal: false,
                        borderRadius: 5,
                        borderRadiusApplication: 'end',
                        columnWidth: '55%',
                    },
                },
                stroke: { show: true, width: 2, colors: ['transparent'] },
                colors: [CHART_COLORS.primary, CHART_COLORS.secondary],
                chart: {
                    height: chartHeight,
                    toolbar: { show: false },
                },
                yaxis: {
                    min: 0,
                    max: 4.5,
                    tickAmount: 9,
                    labels: { formatter: (val) => val?.toFixed(1), style: { fontSize: '13px' } },
                },
                xaxis: {
                    labels: {
                        rotate: -45,
                        rotateAlways: true,
                        trim: false,
                        style: { fontSize: '11px' },
                        maxHeight: 160,
                    },
                },
                legend: { show: true, position: 'top' },
                dataLabels: { enabled: false },
                tooltip: {
                    enabled: true,
                    shared: true,
                    custom: ({ series, dataPointIndex }) => {
                        const entity = consolidatedRows[dataPointIndex]?.name ?? '';
                        const septRating = series[0]?.[dataPointIndex];
                        const decRating = series[1]?.[dataPointIndex];
                        const fmt = (v) => (v != null && v > 0 ? v.toFixed(1) : '—');
                        return `
                            <div class="chart-tooltip">
                                <div class="tooltip-header"><strong>${entity}</strong></div>
                                <div class="tooltip-body">
                                    <div class="tooltip-metric">
                                        <span class="tooltip-swatch" style="background:${CHART_COLORS.primary}"></span>
                                        September: <strong>${fmt(septRating)}</strong>
                                    </div>
                                    <div class="tooltip-metric">
                                        <span class="tooltip-swatch" style="background:${CHART_COLORS.secondary}"></span>
                                        December: <strong>${fmt(decRating)}</strong>
                                    </div>
                                </div>
                            </div>
                        `;
                    },
                },
            },
        }),
        [consolidatedRows, chartHeight]
    );

    // Extracted from the render body as early returns (rather than a nested ternary chain) so
    // each case reads independently instead of compounding nesting/cognitive complexity.
    const renderReportBody = () => {
        if (!selectedYear) {
            return html`<div class="text-center py-5 text-muted small">
                Select a year to load the report.
            </div>`;
        }
        if (isLoading) {
            return html`<${LoaderWithTitle}
                busyLoading=${true}
                title="Loading consolidated report..."
            />`;
        }
        if (!anyPeriodExists) {
            return html`<div class="text-center py-5 text-muted small">
                No periods exist for ${selectedYear} yet.
            </div>`;
        }
        if (!chartData) {
            return html`<div class="text-center py-5 text-muted small">
                No legal entities found.
            </div>`;
        }
        return html`
            ${
                decemberPeriodId === null
                    ? html`<div class="text-end pe-2 pb-1 small text-muted">
                          No December period exists for ${selectedYear} yet.
                      </div>`
                    : ''
            }
            ${
                septemberPeriodId === null
                    ? html`<div class="text-end pe-2 pb-1 small text-muted">
                          No September period exists for ${selectedYear} yet.
                      </div>`
                    : ''
            }
            <${BarChart}
                key=${selectedYear}
                data=${chartData}
                config=${chartConfig}
                isLoading=${false}
                noDataMessage="No SARB ratings have been captured yet for this period"
                css="consolidated-report-chart"
            />
        `;
    };

    return html`
        <div class="consolidated-report-screen">
            <div class="breadcrumb mb-4">
                <a class="breadcrumb-item" href="#" onClick=${handleBreadcrumbBack}>
                    <span class="breadcrumb-title">SARB Self-Assessment</span>
                </a>
                <a class="breadcrumb-item active">
                    <span class="breadcrumb-title">Consolidated Report</span>
                </a>
            </div>

            <div class="card">
                <div class="card-header bg-white d-flex align-items-center gap-3 flex-wrap">
                    <div class="consolidated-year-dropdown">
                        <${Dropdown}
                            id="consolidated-year"
                            label=${{ text: 'Year' }}
                            field=${{ value: selectedYearOption, setValue: handleYearChange }}
                            options=${yearOptions}
                            settings=${{ type: 'input' }}
                            disabled=${!availableYears.length}
                        />
                    </div>

                    <div class="ms-auto">
                        <${Button}
                            buttonType="tertiary"
                            label=${{ precedent: html`<${Icon} name=${IconNames.OpenInNew} />`, text: 'Export' }}
                            onClick=${handleOpenExport}
                            disabled=${!availableYears.length}
                        />
                    </div>
                </div>

                <div class="card-body p-2">${renderReportBody()}</div>
            </div>
        </div>

        <${ConsolidatedExportModal}
            isVisible=${showExportModal}
            onClose=${handleCloseExport}
            availableYears=${availableYears}
            allPeriods=${allPeriods}
            allEntities=${allEntities}
        />
    `;
};
